import { lazy } from "react";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import PrivateRoute from "./core/components/PrivateRoute";
import PrivateRouteBusiness from "./core/components/PrivateRouteBusiness";

// Admin
const Admin = lazy(() => import("./admin/pages/Admin"));
const SelectiveProcess = lazy(() => import("./admin/pages/SelectiveProcess"));
const ProfileData = lazy(() => import("./admin/pages/ProfileData"));
const Faq = lazy(() => import("./admin/pages/Faq"));
const HelpCenter = lazy(() => import("./admin/pages/HelpCenter"));
const Home = lazy(() => import("./admin/pages/Home"));
const SignInForm = lazy(() => import("./admin/pages/SignInForm"));
const Profile = lazy(() => import("./admin/pages/Profile"));
const ProfileActivity = lazy(() => import("./admin/pages/ProfileActivity"));
const ProfileInformation = lazy(
  () => import("./admin/pages/ProfileInformation"),
);
const ProfilePassword = lazy(() => import("./admin/pages/ProfilePassword"));

// Auth
const ForgotPassword = lazy(() => import("./auth/pages/ForgotPassword"));
const ForgotPasswordWhatsapp = lazy(
  () => import("./auth/pages/ForgotPasswordWhatsapp"),
);
const ForgotPasswordSubmit = lazy(
  () => import("./auth/pages/ForgotPasswordSubmit"),
);
const Login = lazy(() => import("./auth/pages/Login"));
const Jobs = lazy(() => import("./auth/pages/Jobs"));
const Register = lazy(() => import("./auth/pages/Register"));

// Calendar
const CalendarApp = lazy(() => import("./calendar/pages/CalendarApp"));

// Tests
const TestsListPage = lazy(() => import("./tests/pages/TestsListPage"));
const TestTakePage = lazy(() => import("./tests/pages/TestTakePage"));
const GuestTestEntry = lazy(() => import("./tests/pages/GuestTestEntry"));
const GuestTestLayout = lazy(() => import("./tests/layouts/GuestTestLayout"));

// Core
const Forbidden = lazy(() => import("./core/pages/Forbidden"));
const NotFound = lazy(() => import("./core/pages/NotFound"));
const UnderConstructions = lazy(
  () => import("./core/pages/UnderConstructions"),
);

// Landing
const Landing = lazy(() => import("./landing/pages/Landing"));

// Home (não logada - MVVM)
const PublicHome = lazy(() => import("./home"));
const PublicLayout = lazy(() => import("./home/PublicLayout"));
const Sobre = lazy(() => import("./home/pages/Sobre"));
const Servicos = lazy(() => import("./home/pages/Servicos"));
const ComoChegar = lazy(() => import("./home/pages/ComoChegar"));
const PoliticaDePrivacidade = lazy(
  () => import("./home/pages/PoliticaDePrivacidade"),
);
const TermosDeUso = lazy(() => import("./home/pages/TermosDeUso"));

// Users
const UserManagement = lazy(() => import("./users/pages/UserManagement"));

// Empresa
const EmpresaGatePage = lazy(() => import("./empresa/pages/EmpresaGatePage"));
const EmpresaLoginPage = lazy(() => import("./empresa/pages/EmpresaLoginPage"));
const EmpresaLayout = lazy(() => import("./empresa/layout/EmpresaLayout"));
const EmpresaInicioPage = lazy(
  () => import("./empresa/pages/EmpresaInicioPage"),
);
const EmpresaVagasPage = lazy(() => import("./empresa/pages/EmpresaVagasPage"));
const EmpresaVagaDetailPage = lazy(
  () => import("./empresa/pages/EmpresaVagaDetailPage"),
);
const EmpresaCurriculosPage = lazy(
  () => import("./empresa/pages/EmpresaCurriculosPage"),
);
const EmpresaAbrirVagaPage = lazy(
  () => import("./empresa/pages/EmpresaAbrirVagaPage"),
);
const EmpresaCandidatoDetailPage = lazy(
  () => import("./empresa/pages/EmpresaCandidatoDetailPage"),
);

function NavigateTo404() {
  const location = useLocation();
  console.log(
    "[404 catch-all] pathname:",
    location.pathname,
    "fullPath:",
    location.pathname + location.search,
  );
  return <Navigate to={`/${process.env.PUBLIC_URL || ""}/404`} replace />;
}

const AppRoutes = () => {
  return (
    <Routes basename="/">
      <Route
        path="/"
        element={
          <PublicLayout>
            <PublicHome />
          </PublicLayout>
        }
      />
      <Route
        path="/candidato/testes/:dispatchId/:accessToken"
        element={<GuestTestLayout />}
      >
        <Route index element={<TestTakePage />} />
      </Route>
      <Route
        path="/candidato/testes/:dispatchAccess"
        element={<GuestTestLayout />}
      >
        <Route index element={<GuestTestEntry />} />
      </Route>
      <Route path="/candidato" element={<PrivateRoute element={<Admin />} />}>
        <Route index element={<Home />} />
        <Route path="cadastro" element={<SignInForm />} />
        <Route path="calendar" element={<CalendarApp />} />
        <Route path="testes" element={<TestsListPage />} />
        <Route path="testes/:dispatchId" element={<TestTakePage />} />
        <Route path="processo" element={<SelectiveProcess />} />
        <Route path="meus-dados" element={<ProfileData />} />
        <Route path="faq" element={<Faq />} />
        <Route path="help" element={<HelpCenter />} />
        <Route path="profile" element={<Profile />}>
          <Route index element={<ProfileActivity />} />
          <Route path="information" element={<ProfileInformation />} />
          <Route path="password" element={<ProfilePassword />} />
        </Route>
        <Route
          path="projects"
          element={
            <Navigate
              to={`/${process.env.PUBLIC_URL || ""}/under-construction`}
              replace
            />
          }
        />
        <Route path="vagas" element={<UserManagement />} />
      </Route>
      <Route path="forgot-password" element={<ForgotPassword />} />
      <Route
        path="forgot-password/whatsapp"
        element={<ForgotPasswordWhatsapp />}
      />
      <Route path="forgot-password-submit" element={<ForgotPasswordSubmit />} />
      <Route path="login" element={<Login />} />
      <Route
        path="vaga/:id"
        element={
          <PublicLayout>
            <Jobs />
          </PublicLayout>
        }
      />
      <Route
        path="vagas/:id"
        element={
          <PublicLayout>
            <Jobs />
          </PublicLayout>
        }
      />
      <Route
        path="sobre"
        element={
          <PublicLayout>
            <Sobre />
          </PublicLayout>
        }
      />
      <Route
        path="servicos"
        element={
          <PublicLayout>
            <Servicos />
          </PublicLayout>
        }
      />
      <Route
        path="como-chegar"
        element={
          <PublicLayout>
            <ComoChegar />
          </PublicLayout>
        }
      />
      <Route
        path="politica-de-privacidade"
        element={
          <PublicLayout>
            <PoliticaDePrivacidade />
          </PublicLayout>
        }
      />
      <Route
        path="termos-de-uso"
        element={
          <PublicLayout>
            <TermosDeUso />
          </PublicLayout>
        }
      />
      <Route path="register" element={<Register />} />
      <Route
        path="/empresa"
        element={
          <PrivateRouteBusiness>
            <EmpresaLayout />
          </PrivateRouteBusiness>
        }
      >
        <Route index element={<EmpresaGatePage />} />
        <Route path="login" element={<EmpresaLoginPage />} />
        <Route path="inicio" element={<EmpresaInicioPage />} />
        <Route path="vagas" element={<EmpresaVagasPage />} />
        <Route
          path="vagas/:codigo/candidatos/:candidatoId"
          element={
            // eslint-disable-next-line react/jsx-no-undef
            <EmpresaCandidatoDetailPage />
          }
        />
        <Route path="vagas/:codigo" element={<EmpresaVagaDetailPage />} />
        <Route path="curriculos" element={<EmpresaCurriculosPage />} />
        <Route path="abrir-vaga" element={<EmpresaAbrirVagaPage />} />
      </Route>
      <Route path="under-construction" element={<UnderConstructions />} />
      <Route path="403" element={<Forbidden />} />
      <Route path="404" element={<NotFound />} />
      <Route path="*" element={<NavigateTo404 />} />
    </Routes>
  );
};

export default AppRoutes;
